import React, {
    useRef,
    useCallback,
    cloneElement,
    isValidElement,
    Children,
    MouseEvent,
    ReactElement,
    HTMLProps,
    useState,
    useEffect,
} from 'react';
import FocusTrap from 'focus-trap-react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import useKey from 'react-use/lib/useKey';
import useToggle from 'react-use/lib/useToggle';
import styled from 'styled-components';
import { Box, BoxProps } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';
import type { Options } from '@popperjs/core';

interface MenuItemProps
    extends FlexProps,
        Omit<HTMLProps<HTMLDivElement>, 'color' | 'width' | 'height'> {
    disableClose?: boolean;
}

export const MenuItem = styled(Flex)<MenuItemProps>`
    width: 100%;
    cursor: pointer;
    outline: none;
    -webkit-user-select: none;
    user-select: none;
    background: none;
    border: none;
    -webkit-tap-highlight-color: transparent;
    transition:
        transform 0.1s ease-out,
        background-color 0.1s ease-out;

    &.active,
    &:active {
        transform: scale(0.96);
    }

    &:hover {
        background-color: var(--background-secondary);
    }
`;

MenuItem.defaultProps = {
    tabIndex: 1,
    as: 'button',
    px: { xs: 1.5, md: 2 },
    py: { xs: 1.5, md: 1.5 },
};

const Dropdown = styled(Flex).attrs({
    direction: 'column',
    overflow: 'hidden',
    alignItems: 'flex-start',
    justifyContent: { xs: 'space-around', sm: 'flex-start' },
    borderRadius: 1.5,
    border: '1px solid var(--divider)',
    background: 'var(--background-primary)',
})`
    box-shadow:
        0 10px 20px -5px rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(0, 0, 0, 0.05);
`;

type MenuProps = Partial<BoxProps> & {
    trigger: ReactElement;
    onTriggerClick?: () => void;
    children:
        | ReactElement<MenuItemProps>
        | Array<ReactElement<MenuItemProps> | undefined>
        | undefined;
    options?: Omit<Partial<Options>, 'modifiers'>;
    root?: HTMLElement;
};

export function Menu(props: MenuProps) {
    const {
        trigger,
        onTriggerClick,
        options = { placement: 'bottom-end' },
        children,
        root,
        ...other
    } = props;
    const [open, toggleOpen] = useToggle(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, options);
    const menuItemsCount = Children.toArray(children).filter(isValidElement).length;

    const onTriggerClickCallback = useCallback(
        (event: MouseEvent<any>) => {
            event.preventDefault();
            event.stopPropagation();
            toggleOpen();
            update?.();
            onTriggerClick?.();
        },
        [update, toggleOpen],
    );

    const onClose = useCallback(() => toggleOpen(false), [toggleOpen]);

    useKey('Escape', onClose, {}, [onClose]);

    useEffect(() => {
        if (!referenceElement || !popperElement) {
            return;
        }

        const onClickAway = (e: Event) => {
            if (
                !e.target ||
                popperElement === e.target ||
                referenceElement === e.target ||
                popperElement.contains(e.target as any) ||
                referenceElement.contains(e.target as any)
            ) {
                return;
            }

            onClose();
            e.stopPropagation();
        };

        const clickOutRoot = root || window;
        clickOutRoot.addEventListener('touchstart', onClickAway, { capture: true });
        clickOutRoot.addEventListener('mousedown', onClickAway, { capture: true });

        return () => {
            clickOutRoot.removeEventListener('touchstart', onClickAway, { capture: true });
            clickOutRoot.removeEventListener('mousedown', onClickAway, { capture: true });
        };
    }, [referenceElement, popperElement, onClose]);

    if (menuItemsCount === 0) {
        return null;
    }

    return (
        <Box ref={containerRef} {...other}>
            {cloneElement(trigger, { ref: setReferenceElement, onClick: onTriggerClickCallback })}
            {open &&
                createPortal(
                    <FocusTrap>
                        <Box
                            ref={setPopperElement}
                            zIndex={10}
                            style={styles.popper}
                            {...attributes.popper}
                        >
                            <Box style={styles.offset} display={open ? 'block' : 'none'} pt={1}>
                                <Dropdown>
                                    {Children.map(children, (child) =>
                                        isValidElement(child)
                                            ? cloneElement(child, {
                                                  onClick: (event: MouseEvent<any>) => {
                                                      event.preventDefault();
                                                      event.stopPropagation();

                                                      if (!child.props.disableClose) {
                                                          onClose();
                                                      }

                                                      child.props.onClick?.(event);
                                                  },
                                              })
                                            : null,
                                    )}
                                </Dropdown>
                            </Box>
                        </Box>
                    </FocusTrap>,
                    root || document.body,
                )}
        </Box>
    );
}
